import { forwardRef } from "react";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";

import Autocomplete from "@mui/material/Autocomplete";

import ArrowDownIcon from "@mui/icons-material/ExpandMoreOutlined";

import { SxProps } from "@mui/system";
import { appColors } from "../../../theme";
import { SelectOptionType } from "../forms/form-types";
import { Stack, TextField } from "@mui/material";

interface OptionType {
  id: string;
  value: string;
  label: string;
}
interface OwnSelectProps {
  label: string;
  labelPosition?: "top" | "left";
  labelAlignment?: "center" | "inherit" | "right" | "left" | "justify";
  labelWidth?: string;
  fieldWidth?: string;
  fontWeight?: number;
  options?: OptionType[];
  isOptionsNotSorted?: boolean;
  placeholderColor?: string;
  helperText?: string;
  containerStyles?: SxProps;
}

type AppSelectProps = OwnSelectProps & Omit<any, "label | containerStyles">;

const AppSelect = forwardRef<HTMLInputElement, AppSelectProps>(
  (
    {
      label,
      options,
      isOptionsNotSorted,
      placeholderColor,
      helperText,
      containerStyles,
      labelWidth,
      fieldWidth,
      labelPosition,
      labelAlignment,
      fontWeight,
      required = false,
      ...rest
    },
    ref
  ) => {
    const defaultOption = rest.value
      ? options?.find((option: any) => option.id === rest.value)
      : null;

    const tempOptions: OptionType[] = options ? [...options] : [];

    const sortedOptions = tempOptions
      ? tempOptions.sort((a, b) => a.label.localeCompare(b.label))
      : [];

    return (
      <FormControl sx={{ ...containerStyles, width: "100%" }}>
        <Stack
          direction={
            labelPosition && labelPosition === "top" ? "column" : "row"
          }
          gap={labelPosition && labelPosition === "top" ? 0.5 : 1.5}
          justifyContent="space-between"
          alignItems={
            labelPosition && labelPosition === "top" ? "flex-start" : "center"
          }
        >
          <Typography
            color={appColors.formText}
            component="label"
            htmlFor={rest.name}
            fontSize={labelPosition && labelPosition === "top" ? 13 : 15}
            width={labelWidth ? labelWidth : 130}
            fontWeight={fontWeight ? fontWeight : 600}
            align={labelAlignment ? labelAlignment : "right"}
            whiteSpace="nowrap"
          >
            {label}{" "}
            {required && (
              <Typography component="small" color="error.main">
                *
              </Typography>
            )}
          </Typography>
          <Stack width={fieldWidth ? fieldWidth : 400}>
            <Autocomplete
              disabled={rest.disabled}
              id={rest.name}
              ref={ref}
              value={defaultOption}
              defaultValue={defaultOption}
              fullWidth={true}
              onChange={(event, newValue) => {
                rest.onChange(newValue?.value);
              }}
              options={
                isOptionsNotSorted === true ? options ?? [] : sortedOptions
              }
              sx={{
                "& .MuiAutocomplete-clearIndicator": {
                  display: "none", // Hide the clear (x) button
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={rest.placeholder}
                  name={rest.name}
                  InputLabelProps={{
                    style: {
                      fontSize: "14px",
                      marginTop: "-3px",
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem
                  {...props}
                  key={option.id}
                  sx={{ width: "100%", fontSize: 14 }}
                >
                  {option.label}
                </MenuItem>
              )}
            />

            {rest.error && (
              <FormHelperText
                sx={{ color: (theme) => theme.palette.error.main }}
              >
                {helperText}
              </FormHelperText>
            )}
          </Stack>
        </Stack>
      </FormControl>
    );
  }
);

export default AppSelect;
